import {
  DEFAULT_CURRENCY_CODE,
  DestroyRef,
  Inject,
  Injectable,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { CheckoutFacade } from '@checkout/data-access';
import {
  CHECKOUT_CONFIG,
  CheckoutConfig,
  CmsCardPage,
  PAYSAFE_CONFIG,
  Paysafe,
  PaysafeConfig,
  PaysafeFields,
  PaysafeForm,
  PaysafeFormField,
  PaysafeFormValue,
  PaysafeInstance,
  PaysafeSetupOptions,
  PaysafeTokenizationOptions,
  defaultPaysafeSetupOptions,
} from '@checkout/util-models';
import { BasketFacade, QuoteDetails } from '@domgen/dgx-fe-basket';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import {
  CustomerDetails,
  DocumentSendMethod,
  filterNullUndefined,
} from '@domgen/dgx-fe-common';
import { controlValue, formValue } from '@domgen/dgx-fe-forms/shared';
import {
  CurrencyCode,
  USProvinceName,
  formatProvinceCode,
} from '@domgen/dgx-fe-i18n';
import { NotificationsFacade } from '@domgen/dgx-fe-notifications';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, combineLatest, from, of, throwError } from 'rxjs';
import { finalize, first, map, mergeMap, tap } from 'rxjs/operators';
import { PaysafeFormConfig } from '../../models/paysafe/paysafe-form-config.interface';
import { formatPaysafeTokenizeOptions } from '../../utils/paysafe-tokenization-options/paysafe-tokenization-options.utils';
import { PaysafeFormBuilder } from '../paysafe-form-builder/paysafe-form-builder.service';

declare global {
  interface Window {
    paysafe: Paysafe;
  }
}

export interface ViewModel {
  cmsData: CmsCardPage;
  fields?: PaysafeFields;
  form: FormGroup<PaysafeForm>;
  formValue: PaysafeFormValue;
  loading: boolean;
  monthlyRenewalVisible: boolean;
  purchasing: boolean;
  selectedQuotes: QuoteDetails[];
  validatingCardDetails: boolean;
}

export interface ComponentState {
  firstSelectedQuote?: QuoteDetails;
  form?: FormGroup<PaysafeForm>;
  instance?: PaysafeInstance;
  loading: boolean;
  monthlyRenewalVisible: boolean;
  purchasing: boolean;
  validatingCardDetails: boolean;
}

@Injectable()
export class PaysafeFormComponentService extends ComponentStore<ComponentState> {
  private readonly monthlyRenewalVisible$: Observable<boolean> = this.select(
    (state: ComponentState) => state.monthlyRenewalVisible
  );

  private readonly fields$: Observable<PaysafeFields | undefined> = this.select(
    (state: ComponentState) => state.instance?.fields
  );

  private readonly formConfig$: Observable<PaysafeFormConfig> = combineLatest({
    customerDetails: this.checkoutFacade.customerDetails$,
    termsAndConditionsFeatureEnabled: of(
      this.checkoutConfig.features.checkoutTermsAndConditions
    ),
    monthlyRenewalVisible: this.monthlyRenewalVisible$,
  });

  private readonly form$: Observable<FormGroup<PaysafeForm>> = this.select(
    (state: ComponentState) => state.form
  ).pipe(filterNullUndefined());

  private readonly instance$: Observable<PaysafeInstance> = this.select(
    (state: ComponentState) => state.instance
  ).pipe(filterNullUndefined());

  private readonly loading$: Observable<boolean> = this.select(
    (state: ComponentState) => state.loading
  );

  private readonly purchasing$: Observable<boolean> = this.select(
    (state: ComponentState) => state.purchasing
  );

  private readonly validatingCardDetails$: Observable<boolean> = this.select(
    (state: ComponentState) => state.validatingCardDetails
  );

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsData: this.cmsContainer.content$,
    fields: this.fields$,
    form: this.form$,
    formValue: this.form$.pipe(formValue<PaysafeFormValue>()),
    loading: this.loading$,
    monthlyRenewalVisible: this.monthlyRenewalVisible$,
    purchasing: this.purchasing$,
    selectedQuotes: this.basketFacade.selectedQuotes$,
    validatingCardDetails: this.validatingCardDetails$,
  });

  constructor(
    @Inject(CHECKOUT_CONFIG) private readonly checkoutConfig: CheckoutConfig,
    @Inject(DEFAULT_CURRENCY_CODE) private readonly currencyCode: CurrencyCode,
    @Inject(PAYSAFE_CONFIG) private readonly paysafeConfig: PaysafeConfig,
    private readonly basketFacade: BasketFacade,
    private readonly checkoutFacade: CheckoutFacade,
    private readonly destroyRef: DestroyRef,
    private readonly cmsContainer: CmsContainer<CmsCardPage>,
    private readonly notificationsFacade: NotificationsFacade,
    private readonly paysafeFormBuilder: PaysafeFormBuilder
  ) {
    super({
      monthlyRenewalVisible: false,
      loading: true,
      purchasing: false,
      validatingCardDetails: false,
    });
  }

  private readonly updateMonthlyRenewalVisible = this.updater(
    (state: ComponentState, monthlyRenewalVisible: boolean) => ({
      ...state,
      monthlyRenewalVisible,
    })
  )(
    this.checkoutFacade.customerDetails$.pipe(
      map((customerDetails: CustomerDetails | undefined) =>
        formatProvinceCode(
          customerDetails?.address.addressLine4,
          USProvinceName
        )
      ),
      filterNullUndefined(),
      map(
        (customerUsState: string) =>
          this.checkoutConfig.monthlyRenewalFieldEnabledStates?.includes(
            customerUsState
          ) ?? false
      )
    )
  );

  private readonly updateFirstSelectedQuote = this.updater(
    (state: ComponentState, firstSelectedQuote?: QuoteDetails) => ({
      ...state,
      firstSelectedQuote,
    })
  )(this.basketFacade.firstSelectedQuote$);

  private readonly updateForm = this.updater(
    (state: ComponentState, form: FormGroup<PaysafeForm>) => ({
      ...state,
      form,
    })
  );

  private readonly updateInstance = this.updater(
    (state: ComponentState, instance: PaysafeInstance) => ({
      ...state,
      instance,
    })
  );

  private readonly updateLoading = this.updater(
    (state: ComponentState, loading: boolean) => ({
      ...state,
      loading,
    })
  );

  private readonly updatePurchasing = this.updater(
    (state: ComponentState, purchasing: boolean) => ({
      ...state,
      purchasing,
    })
  )(this.checkoutFacade.purchasing$);

  private readonly updateValidatingCardDetails = this.updater(
    (state: ComponentState, validatingCardDetails: boolean) => ({
      ...state,
      validatingCardDetails,
    })
  );

  readonly buildForm = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      mergeMap(() => this.formConfig$),
      first(),
      map((config: PaysafeFormConfig) =>
        this.paysafeFormBuilder.buildPaysafeForm(config)
      ),
      tap((form) => this.updateForm(form))
    )
  );

  readonly show = this.effect((instance$: Observable<PaysafeInstance>) =>
    instance$.pipe(
      mergeMap((instance: PaysafeInstance) => from(instance.show())),
      tap(() => this.updateLoading(false))
    )
  )(this.instance$);

  private readonly updateDocumentSendMethod = this.effect(
    (trigger$: Observable<boolean | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        tap((paperlessPreference: boolean) =>
          this.checkoutFacade.updateDocumentSendMethod(
            paperlessPreference
              ? DocumentSendMethod.Both
              : DocumentSendMethod.Email
          )
        )
      )
  )(
    this.form$.pipe(controlValue<boolean>(PaysafeFormField.PaperlessPreference))
  );

  setup(): void {
    const setupOptions: PaysafeSetupOptions = {
      ...defaultPaysafeSetupOptions,
      accounts: {
        default: this.paysafeConfig.accountId,
      },
      currencyCode: this.currencyCode,
      environment: this.paysafeConfig.environment,
    };

    from(window.paysafe.fields.setup(this.paysafeConfig.key, setupOptions))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (instance: PaysafeInstance) => this.updateInstance(instance),
        error: (error: Error) =>
          this.notificationsFacade.sendErrorNotification(error.message),
      });
  }

  tokenizeFormValue(formValue: PaysafeFormValue): Observable<string> {
    const options: PaysafeTokenizationOptions | undefined =
      formatPaysafeTokenizeOptions(formValue, this.firstSelectedQuote);

    if (!options) {
      return throwError(() => new Error());
    }

    this.updateValidatingCardDetails(true);

    return this.instance$.pipe(
      first(),
      mergeMap((instance: PaysafeInstance) => from(instance.tokenize(options))),
      map(({ token }) => token),
      tap({
        error: (error: Error) =>
          this.notificationsFacade.sendErrorNotification(error.message),
      }),
      finalize(() => this.updateValidatingCardDetails(false))
    );
  }

  private get firstSelectedQuote(): QuoteDetails | undefined {
    return this.get((state) => state.firstSelectedQuote);
  }
}
